import { get, patch, post } from '@/Utils/API';
import {
  LINK_VIEWS_SUPPORT,
  MODAL_CREATE_ACCOUNT,
  MODAL_CREATE_SHEET_FROM_VIEW,
  MODAL_DELETE_VIEW,
  MODAL_RENAME_VIEW,
  MODAL_URL_FOR_BI_AND_SPREADSHEETS,
  TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_SUCCESS,
  URL_DATASET,
  URL_SPREADSHEET,
} from '@/Utils/constants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import ThreeDotsLoader from '../Loader/ThreeDotsLoader/ThreeDotsLoader';
import { Button } from '../UI/Button/Button';

import showToast from '@/Components/Toast/showToastTemplate';
import Tooltip from '@/Components/UI/Tooltip/Tooltip';
import { spreadsheetSliceName, userDataSliceName } from '@/redux/constants';
import { showModal } from '@/redux/reducers/modalsSlice';
import { fetchLiveSharesEntries, getAllViews } from '@/redux/reducers/spreadsheetSlice';
import { AppDispatch } from '@/redux/store';
import { isRevenueBaseFile, openInNewTab } from '@/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../UI/Icon/Icon';
import CreateView from './CreateView';
import { useCurrentFile } from '@/hooks/useCurrentFile';

type Position = {
  left: number;
  top: number;
};

type User = {
  email: string;
  is_anonymous: boolean;
  userProperties?: {
    revenueBaseAccess?: boolean;
  };
};

export type View = {
  name: string;
  id: string;
  client_state: string;
  file: string;
  owner: string;
};

type LiveSharesEntry = {
  ClientStateVersionId: string;
  Handle: string;
  ShareId: string;
  Timestamp: string;
  UserId: string;
};

export type ContextMenuParams = {
  name: string | undefined;
  uuid: string | undefined;
  stateVersion: string | undefined;
};

type ContextMenu = {
  view: View;
  loading: boolean;
  setLoading: (v: boolean) => void;
  close?: () => void;
  setIsContextMenuOpen: (v: boolean) => void;
  params?: ContextMenuParams;
};

const DEFAULT_POSITION = { left: 0, top: 0 };

const Views = () => {
  const { sheetViews } = useSelector(
    (state: { spreadsheet: { sheetViews: View[] } }) => state[spreadsheetSliceName]
  );
  const { liveSharesEntries } = useSelector(
    (state: { spreadsheet: { liveSharesEntries: LiveSharesEntry[] } }) =>
      state[spreadsheetSliceName]
  );
  const { user } = useSelector(
    (state: { [userDataSliceName]: { user: User } }) => state[userDataSliceName]
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [selectedView, setSelectedView] = useState<View | null>();
  const [activeView, setActiveView] = useState<string | null>(null);
  const [position, setPosition] = useState<Position>(DEFAULT_POSITION);
  const [isSelectedViewEnabled, setIsSelectedViewEnabled] = useState(false);
  const [selectedViewLiveShare, setSelectedViewLiveShare] = useState<LiveSharesEntry | null>(null);

  const [isEditing, setIsEditing] = useState(false);

  const [currentView, setCurrentView] = useState<View | null>(null);

  const elementRef = useRef<HTMLDivElement>(null);
  const { name, uuid, fileUuid: stateVersion } = useParams();

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();
  const params = useParams();

  const handleAddViewButtonClick = () => {
    if (user?.is_anonymous) {
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    }
    setIsEditing(true);
  };

  const refreshViews = () => {
    dispatch(getAllViews());
    dispatch(fetchLiveSharesEntries());
  };

  const handleViewItemClick = (clientStateId: string) => {
    if (!isContextMenuOpen) {
      navigate(`/${URL_SPREADSHEET}/${name}/${uuid}/${clientStateId}`);
      window.location.reload();
    } else {
      resetContextMenu();
    }
  };

  const handleUpdateViewButtonClick = async (e: React.MouseEvent, viewId: string) => {
    e.stopPropagation();
    setLoading(true);

    let endpoint = '';
    let payload = {};
    try {
      const clientStateVersion = await getClientStateVersion();

      payload = {
        client_state_id: clientStateVersion,
      };

      endpoint = `${URL_DATASET}/${getFileUuidFromPath()}/views/${viewId}`;
      const response = await patch(endpoint, payload);

      if (response.hasOwnProperty('Success') && !response.Success) {
        throw new Error();
      }

      showToast({
        type: TOAST_TYPE_SUCCESS,
        text: 'View updated successfully',
        errorContext: null,
      });
    } catch (error) {
      showToast({
        type: TOAST_TYPE_ERROR,
        text: 'Failed to update view. Please try again later or contact support.',
        errorContext: error,
        endpoint: endpoint,
        payload: payload,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    } finally {
      setLoading(false);
      setIsContextMenuOpen(false);
    }
  };

  const handleContextMenuClick = (e: React.MouseEvent, view: View) => {
    e.stopPropagation();
    setIsEditing(false);
    setSelectedView(view);
    const { offsetLeft, offsetTop } = e.target as HTMLDivElement;
    const scrollTop = elementRef.current?.scrollTop ?? 0;
    setPosition({ left: offsetLeft + 23, top: offsetTop - scrollTop });
  };

  const resetContextMenu = () => {
    setSelectedView(null);
    setPosition(DEFAULT_POSITION);
  };

  const refreshViewLiveShare = async () => {
    const entry = liveSharesEntries.find(
      (ls) => ls.ClientStateVersionId === selectedView?.client_state
    );
    if (entry) {
      setSelectedViewLiveShare(entry);
    }
  };

  const checkIfViewIsEnabled = async () => {
    const isEnabled = liveSharesEntries.some(
      (ls) => ls.ClientStateVersionId === selectedView?.client_state
    );
    setIsSelectedViewEnabled(isEnabled);
  };

  useEffect(() => {
    dispatch(getAllViews());
    dispatch(fetchLiveSharesEntries());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchLiveSharesEntries());
  }, [selectedView]);

  useEffect(() => {
    checkIfViewIsEnabled();
    refreshViewLiveShare();
  }, [liveSharesEntries]);

  const getClientStateVersion = async () => {
    const response = await get(`client-state/${getFileUuidFromPath()}/current-version`);
    if (response?.version) {
      return response.version;
    } else {
      throw new Error();
    }
  };

  useEffect(() => {
    const targetView = sheetViews.find(({ id }) => id === params.fileUuid);

    if (targetView?.name) {
      setActiveView(targetView.name);
      setCurrentView(targetView);
    } else {
      setActiveView(null);
    }
  }, [params, sheetViews]);

  return (
    <div className='text-sm' data-cy='sheet-views-dropdown'>
      <div className='flex items-center justify-between py-1'>
        <div className='text-sm font-semibold text-midnight-500'>Views</div>
        <div className='text-ui-helper'>
          <Button
            variant='ghost'
            iconName='question'
            size='small'
            className='!outline-none !ring-0'
            onClick={() => {
              openInNewTab(LINK_VIEWS_SUPPORT);
            }}
          />
        </div>
      </div>
      <div className='relative'>
        {loading && (
          <div
            className='absolute top-0 bottom-0 left-0 right-0 z-10 flex items-center justify-center bg-white bg-opacity-50'
            data-cy='loader'
          >
            <ThreeDotsLoader text='' className='flex items-center justify-center h-6 ' />
          </div>
        )}
        <div ref={elementRef} onScroll={resetContextMenu}>
          {!sheetViews.length && !loading && (
            <div
              className='flex items-center justify-center h-12 cursor-default text-ui-helper'
              data-cy='no-views-text'
            >
              No Views
            </div>
          )}

          {sheetViews.length ? (
            <ul>
              {currentView ? (
                <div className='flex items-center justify-between w-full p-2 font-medium align-middle cursor-default'>
                  <div className='flex items-center gap-1 truncate max-w-72'>
                    <div data-cy={`item-${currentView?.name}`} className='truncate'>
                      {currentView?.name || ''}
                    </div>
                  </div>
                  <div className='flex items-center ml-2'>
                    <Tooltip
                      text={`Owned by ${currentView?.owner}`}
                      side='bottom'
                      className='py-0 text-center max-w-64'
                    >
                      <div
                        className={clsx(
                          'flex justify-center w-6 h-6 pt-[2px] mb-[1px] rounded hover:bg-shadow-300 hover:bg-opacity-24',
                          user.email === currentView?.owner && 'hidden'
                        )}
                      >
                        <Icon name='users-three' color='#474747' size={16} />
                      </div>
                    </Tooltip>

                    <Tooltip text={'Save changes to view'} side='bottom' className='py-0'>
                      <Button
                        iconName='floppy-disk'
                        size='small'
                        variant='ghost'
                        dataCy={`update-view-btn-${currentView?.name}`}
                        onClick={(e) => handleUpdateViewButtonClick(e, currentView?.id)}
                      />
                    </Tooltip>
                    <Tooltip text={'More'} side='bottom' className='py-0'>
                      <Button
                        iconName='dots-three-vertical'
                        size='small'
                        variant='ghost'
                        className={clsx(
                          selectedView?.id === currentView?.id && '!bg-shadow-300 !bg-opacity-24'
                        )}
                        onClick={(e) => handleContextMenuClick(e, currentView)}
                      />
                    </Tooltip>
                  </div>
                </div>
              ) : null}

              {activeView ? <hr className='my-1' /> : null}
              <div className='my-1 overflow-auto max-h-64 min-h-8'>
                {sheetViews
                  .filter((view: View) => view.id !== currentView?.id)
                  .map((view: View) => {
                    return (
                      <li key={view.id} className=''>
                        <button
                          onClick={() => handleViewItemClick(view.id)}
                          className={clsx(
                            'flex justify-between items-center h-8 p-2 rounded hover:bg-shadow-300 hover:bg-opacity-24 cursor-pointer w-full outline-none'
                          )}
                          disabled={loading}
                          data-cy={`item-${view.name}`}
                        >
                          <div className='flex items-center justify-start'>
                            {view?.name?.length > 50 ? (
                              <Tooltip
                                text={view.name}
                                side='bottom'
                                className='w-auto py-0 max-w-64'
                              >
                                <div className='truncate max-w-72'>{view.name}</div>
                              </Tooltip>
                            ) : (
                              <div className='truncate max-w-72'>{view.name}</div>
                            )}
                          </div>
                          <div className='flex items-center justify-center ml-2 text-ui-helper'>
                            <Tooltip
                              text={`Owned by ${view?.owner}`}
                              side='bottom'
                              className='py-0 text-center max-w-64'
                            >
                              <div
                                className={clsx(
                                  'flex justify-center w-6 h-6 pt-[2px] rounded hover:bg-shadow-300 hover:bg-opacity-24',
                                  user.email === view.owner && 'hidden'
                                )}
                              >
                                <Icon name='users-three' color='#474747' size={16} />
                              </div>
                            </Tooltip>
                            <div>
                              <Tooltip text={'More'} side='bottom' className='py-0'>
                                <Button
                                  className={clsx(
                                    '!w-6 !h-6',
                                    selectedView?.id === view.id && '!bg-shadow-300 !bg-opacity-24'
                                  )}
                                  variant='ghost'
                                  dataCy={`item-menu-${view.name}`}
                                  iconName='dots-three-vertical'
                                  onClick={(e) => handleContextMenuClick(e, view)}
                                />
                              </Tooltip>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
              </div>
            </ul>
          ) : null}
        </div>
        {selectedView && (
          <div
            className='absolute '
            style={{ top: `${position.top}px`, left: `${position.left}px` }}
          >
            {
              <ContextMenu
                view={selectedView}
                loading={loading}
                setLoading={setLoading}
                close={resetContextMenu}
                setIsContextMenuOpen={setIsContextMenuOpen}
                params={{ name, uuid, stateVersion }}
              />
            }
          </div>
        )}
      </div>
      <hr className='my-2' />
      {isEditing ? (
        <CreateView setIsEditing={setIsEditing} refreshViews={refreshViews} />
      ) : (
        <Button
          variant='ghost'
          className='w-full'
          color='midnight'
          onClick={handleAddViewButtonClick}
          dataCy='add-view-popover-button'
          iconName='plus-circle'
        >
          New View
        </Button>
      )}
    </div>
  );
};

export default Views;

const ContextMenu = ({
  view,
  loading,
  setLoading,
  close = () => {},
  setIsContextMenuOpen,
  params,
}: ContextMenu) => {
  const { user } = useSelector(
    (state: { [userDataSliceName]: { user: User } }) => state[userDataSliceName]
  );

  const [isCopied, setIsCopied] = useState(false);
  const currentFile = useCurrentFile();
  const isRevenueBaseLimitedView =
    !!user?.userProperties?.revenueBaseAccess && isRevenueBaseFile(currentFile);

  const dispatch = useDispatch<AppDispatch>();

  // Refs for managing clicks on each button
  const isDuplicateProcessing = useRef(false);
  const isShareProcessing = useRef(false);

  const handleDeleteView = () => {
    dispatch(showModal({ name: MODAL_DELETE_VIEW, props: { view, params } }));
  };

  const handleDuplicateButtonClick = async () => {
    if (isDuplicateProcessing.current) return;
    isDuplicateProcessing.current = true;

    setLoading(true);
    let endpoint = '';
    let payload = {};
    try {
      const clientStateId = view.client_state;

      payload = {
        client_state_id: clientStateId,
        name: `${view.name} DUPLICATE`,
      };

      endpoint = `${URL_DATASET}/${view.file}/views`;
      const response = await post(endpoint, payload);

      if (response.hasOwnProperty('Success') && !response.Success) {
        throw new Error(response?.Message);
      }
    } catch (error) {
      showToast({
        type: TOAST_TYPE_ERROR,
        text: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
        errorContext: error,
        endpoint: endpoint,
        payload: payload,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    } finally {
      dispatch(getAllViews()).then(() => {
        setLoading(false);
        isDuplicateProcessing.current = false;
        close();
      });
    }
  };

  const handleCreateNewSheetClick = async () => {
    dispatch(showModal({ name: MODAL_CREATE_SHEET_FROM_VIEW, props: { view: view } }));
  };

  const handleLinkDataTo = () => {
    dispatch(
      showModal({ name: MODAL_URL_FOR_BI_AND_SPREADSHEETS, props: { preselectedView: view } })
    );
  };

  const handleShareLinkButtonClick = () => {
    if (isShareProcessing.current) return;
    isShareProcessing.current = true;
    setIsCopied(true);

    navigator.clipboard.writeText(
      `${window.location.origin}/${URL_SPREADSHEET}/${params?.name}/${params?.uuid}/${view.id}`
    );

    isShareProcessing.current = false;

    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  const handleRenameButtonClick = () => {
    if (user?.is_anonymous) {
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    }
    dispatch(showModal({ name: MODAL_RENAME_VIEW, props: { view } }));
  };

  useEffect(() => {
    setIsContextMenuOpen(true);
    return () => {
      setIsContextMenuOpen(false);
    };
  }, []);

  const menuItem =
    'flex items-start align-middle !justify-start w-full !h-auto !min-h-8 p-2 rounded outline-none cursor-pointer hover:bg-shadow-300 hover:bg-opacity-24 text-sm !text-left flex-col';

  return (
    <div className='bg-white rounded p-1 border !w-72 text-ui'>
      <ul data-cy={'sheet-view-context-dropdown'}>
        <li>
          <button onClick={handleShareLinkButtonClick} className={menuItem} disabled={loading}>
            {isCopied ? (
              <div className='flex gap-1'>
                Copied to Clipboard
                <Icon
                  name='check-circle'
                  color='#A3AE28'
                  weight='duotone'
                  size={16}
                  className='mt-[1px]'
                />
              </div>
            ) : (
              <div>Copy Link to View</div>
            )}
            <div className='text-xs text-ui-helper'>URL for easy collaboration</div>
          </button>
        </li>
        {!isRevenueBaseLimitedView && (
          <>
            <li>
              <button onClick={handleLinkDataTo} className={menuItem} disabled={loading}>
                <div>Link Data to</div>
                <div className='text-xs text-ui-helper'>
                  Unlisted public link for BI & Spreadsheets
                </div>
              </button>
            </li>
            <hr className='my-1' />
            <li>
              <button
                onClick={handleCreateNewSheetClick}
                className={menuItem}
                disabled={loading}
                data-cy={'create-link-to-data-btn'}
              >
                <div>Create Linked Sheet from View</div>
                <div className='text-xs text-ui-helper'>Restricted subset of rows and columns</div>
              </button>
            </li>
          </>
        )}
        <hr className='my-1' />
        <li>
          <button
            onClick={handleRenameButtonClick}
            className={menuItem}
            disabled={loading || user.email !== view.owner}
            data-cy={'rename'}
          >
            <div className='flex items-center justify-start gap-2'>
              <Icon name='pencil-simple' size={16} />
              Rename View
            </div>
          </button>
        </li>
        <li>
          <button
            onClick={handleDuplicateButtonClick}
            className={menuItem}
            disabled={loading}
            data-cy={'duplicate'}
          >
            <div className='flex items-center justify-start gap-2'>
              <Icon name='copy' size={16} />
              Duplicate View
            </div>
          </button>
        </li>

        {user.email === view.owner && (
          <>
            <hr className='my-1' />
            <li>
              <button
                onClick={handleDeleteView}
                className={menuItem}
                disabled={loading}
                data-cy={'delete'}
              >
                <div className='flex items-center justify-start gap-2'>
                  <Icon name='trash' size={16} />
                  Delete View
                </div>
              </button>
            </li>
          </>
        )}
      </ul>
    </div>
  );
};
